import { useState } from "react";
import { Plus, Search, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataTable, Column } from "@/components/shared/DataTable";
import { Pagination } from "@/components/shared/Pagination";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { StatusBadge, PriorityBadge, ComplaintStatus, Priority } from "@/components/shared/StatusBadge";
import { useToast } from "@/components/ui/toast";
import { useComplaints, useDeleteComplaint } from "@/hooks/useComplaints";
import { useActiveEmployees } from "@/hooks/useEmployees";
import { useDebounce } from "@/hooks/useDebounce";
import { useAuthStore } from "@/store/auth.store";
import { ComplaintListItem } from "@/api/complaint.api";
import { ComplaintFormDialog } from "./ComplaintFormDialog";
import { ComplaintDetailDialog } from "./ComplaintDetailDialog";
import { isAxiosError } from "axios";

const PAGE_SIZE = 10;
const ALL_STATUSES: ComplaintStatus[] = [
  "PENDING",
  "ASSIGNED",
  "INSPECTION",
  "WAITING_PARTS",
  "REPAIRING",
  "TESTING",
  "COMPLETED",
  "DELIVERED",
  "CANCELLED",
];
const ALL_PRIORITIES: Priority[] = ["LOW", "MEDIUM", "HIGH", "URGENT"];

export function ComplaintsPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<ComplaintStatus | "">("");
  const [priority, setPriority] = useState<Priority | "">("");
  const [employeeId, setEmployeeId] = useState("");

  const debouncedSearch = useDebounce(search, 350);

  const [formOpen, setFormOpen] = useState(false);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [deletingComplaint, setDeletingComplaint] = useState<ComplaintListItem | null>(null);

  const role = useAuthStore((s) => s.user?.role);
  const { toast } = useToast();
  const { data: employees } = useActiveEmployees();

  const { data, isLoading } = useComplaints({
    page,
    limit: PAGE_SIZE,
    search: debouncedSearch || undefined,
    status: status || undefined,
    priority: priority || undefined,
    employeeId: employeeId || undefined,
  });
  const deleteMutation = useDeleteComplaint();

  const baseColumns: Column<ComplaintListItem>[] = [
    {
      key: "number",
      header: "Complaint #",
      render: (c) => <span className="font-data font-medium">{c.complaintNumber}</span>,
    },
    {
      key: "customer",
      header: "Customer",
      render: (c) => (
        <div>
          <p>{c.customer.fullName}</p>
          <p className="font-data text-xs text-muted-foreground">{c.customer.phone}</p>
        </div>
      ),
    },
    { key: "product", header: "Product", render: (c) => c.productName },
    { key: "priority", header: "Priority", render: (c) => <PriorityBadge priority={c.priority} /> },
    { key: "status", header: "Status", render: (c) => <StatusBadge status={c.status} /> },
    {
      key: "employee",
      header: "Technician",
      render: (c) => c.assignedEmployee?.fullName ?? <span className="text-muted-foreground">Unassigned</span>,
    },
    {
      key: "due",
      header: "Due",
      render: (c) => (c.expectedCompletionDate ? format(new Date(c.expectedCompletionDate), "dd MMM") : "—"),
    },
  ];

  const adminColumn: Column<ComplaintListItem> = {
    key: "actions",
    header: "",
    className: "text-right",
    render: (c) => (
      <Button
        variant="ghost"
        size="icon"
        className="hover:text-destructive"
        onClick={(e) => {
          e.stopPropagation();
          setDeletingComplaint(c);
        }}
        aria-label="Delete"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    ),
  };

  const columns = role === "ADMIN" ? [...baseColumns, adminColumn] : baseColumns;

  const handleDelete = async () => {
    if (!deletingComplaint) return;
    try {
      await deleteMutation.mutateAsync(deletingComplaint.id);
      toast({ title: "Complaint deleted", variant: "success" });
      setDeletingComplaint(null);
    } catch (err) {
      const message = isAxiosError(err)
        ? (err.response?.data as { error?: { message?: string } } | undefined)?.error?.message
        : undefined;
      toast({ title: "Could not delete complaint", description: message, variant: "destructive" });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Complaints</h1>
          <p className="text-sm text-muted-foreground">Track every job from intake to delivery.</p>
        </div>
        <Button onClick={() => setFormOpen(true)}>
          <Plus className="h-4 w-4" />
          New complaint
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search complaint #, phone, customer..."
            className="pl-9"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <select
          className="flex h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as ComplaintStatus | "");
            setPage(1);
          }}
        >
          <option value="">All statuses</option>
          {ALL_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          className="flex h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm"
          value={priority}
          onChange={(e) => {
            setPriority(e.target.value as Priority | "");
            setPage(1);
          }}
        >
          <option value="">All priorities</option>
          {ALL_PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        {role === "ADMIN" && (
          <select
            className="flex h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm"
            value={employeeId}
            onChange={(e) => {
              setEmployeeId(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All technicians</option>
            {employees?.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.fullName}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="rounded-lg border border-border bg-card">
        <DataTable
          columns={columns}
          data={data?.items ?? []}
          isLoading={isLoading}
          rowKey={(c) => c.id}
          onRowClick={(c) => setViewingId(c.id)}
          emptyMessage="No complaints match these filters."
        />
        <Pagination page={page} limit={PAGE_SIZE} total={data?.total ?? 0} onPageChange={setPage} />
      </div>

      <ComplaintFormDialog open={formOpen} onOpenChange={setFormOpen} />
      <ComplaintDetailDialog complaintId={viewingId} onOpenChange={(open) => !open && setViewingId(null)} />
      <ConfirmDialog
        open={Boolean(deletingComplaint)}
        onOpenChange={(open) => !open && setDeletingComplaint(null)}
        title="Delete complaint?"
        description={`This will remove complaint ${deletingComplaint?.complaintNumber ?? ""} and its history. This can't be undone.`}
        confirmLabel="Delete"
        isLoading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}
